import React, { useState, useEffect, useRef } from 'react';
import axios from "axios";
import { useSelector } from 'react-redux';
import { selectUser } from "../../features/userSlice";
import styles from './AssignRoles.module.css';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Row, Col, Card, Badge } from "react-bootstrap";
import { FaArrowRightArrowLeft } from "react-icons/fa6";
import RolesTreeStructure from '../../components/assignRoles/RolesTreeStructure';
import FloatingActions from '../../components/FloatingActions';

const AssignRoles = ({ existingData, onSubmit }) => {
  const [userPermission, setUserPermission] = useState("");
  const [empId, setEmpId] = useState("");
  const [copyEmpId, setCopyEmpId] = useState("");
  const [userTreeMenu, setUserTreeMenu] = useState([]);
  const [treeMenu, setTreeMenu] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [loadingMenus, setLoadingMenus] = useState(false);
  const [usersMap, setUsersMap] = useState({});

  const user = useSelector(selectUser);
  const emp_id = user?.emp_id;
  const token = user?.token;
  const navigate = useNavigate();

  const topRef = useRef(null);
  const bottomRef = useRef(null);

  const getUserDisplayName = (code) => {
    if (!code) return "";
    return usersMap[String(code).trim()] || "";
  };

  const formatUserLabel = (code) => {
    const name = getUserDisplayName(code);
    return name ? `${code} - ${name}` : String(code);
  };

  // Available menus the logged-in user can assign (based on can_be_delegated / super-admin list)
  const fetchLoggedInUserMenuDataTree = async () => {
    try {
      const response = await axios.post('/api/menu/getMenuDataCanDelegate', { emp_id });
      setUserTreeMenu(
        (response.data || []).map(item => ({
          ...item,
          showMenu: false,
          can_be_delegated: false,
        }))
      );
    } catch (error) {
      console.error('Error fetching delegatable menu data:', error);
    }
  };

  const fetchUsersMap = async () => {
    try {
      const res = await axios.post('/api/users/getAllUsers');
      const map = {};
      (res.data || []).forEach((u) => {
        const id = String(u.emp_id ?? "").trim();
        if (id) map[id] = u.Name || u.emp_name || u.name || "";
      });
      setUsersMap(map);
    } catch (error) {
      console.error('Error fetching users list:', error);
    }
  };

  const buildTreeFromUserPermissions = (availableMenus, assignedMenus) => {
    const assignedMap = new Map(
      (assignedMenus || []).map(item => [item.menu_id, item])
    );

    return availableMenus.map(item => {
      const assigned = assignedMap.get(item.menu_id);
      const delegated = assigned?.can_be_delegated;
      return {
        ...item,
        showMenu: !!assigned,
        can_be_delegated: delegated === 'Y' || delegated === true || delegated === 'true',
      };
    });
  };

  // Show Roles: load target user's current menu + can_be_delegated into the tree
  const handleShowRoles = async () => {
    if (!empId) {
      alert("Please enter the User Code first!");
      return;
    }
    if (!userTreeMenu.length) {
      alert("No menus available to assign for your account.");
      return;
    }

    setLoadingMenus(true);
    try {
      const response = await axios.post("/api/menu/getMenuUserData", { emp_id: empId });
      const result = buildTreeFromUserPermissions(userTreeMenu, response.data);
      setTreeMenu(result);
      setSelectedRoles([]);
      setUserPermission(empId);
      setCopyEmpId('');
    } catch (error) {
      setUserPermission("");
      setTreeMenu([]);
      console.error('Error fetching user menu data:', error);
      alert('Failed to load roles. Please try again.');
    } finally {
      setLoadingMenus(false);
    }
  };

  // Copy Roles: copy second user's permissions into the form; submit still saves to first user (empId)
  const handleCopyRoles = async () => {
    if (!empId) {
      alert("Please enter the User Code first!");
      return;
    }
    if (!copyEmpId) {
      alert("Please enter the Copy Permissions From user code!");
      return;
    }
    if (!userTreeMenu.length) {
      alert("No menus available to assign for your account.");
      return;
    }

    setLoadingMenus(true);
    try {
      const response = await axios.post("/api/menu/getMenuUserData", { emp_id: copyEmpId });
      const result = buildTreeFromUserPermissions(userTreeMenu, response.data);
      setTreeMenu(result);
      setSelectedRoles([]);
      // Keep target as first user — copy only fills the checkboxes
      setUserPermission(empId);
    } catch (error) {
      console.error('Error copying user menu data:', error);
      alert('Failed to copy roles. Please try again.');
    } finally {
      setLoadingMenus(false);
    }
  };

  const handleRolesSubmit = async () => {
    if (!empId) {
      alert("Please enter the User Code first!");
      return;
    }

    const availableIds = treeMenu.map(item => Number(item.menu_id)).filter(id => id && !Number.isNaN(id));
    const selectedIds = selectedRoles.map(item => Number(item.menu_id)).filter(id => id && !Number.isNaN(id));
    const selectedSet = new Set(selectedIds);
    const removedIds = availableIds.filter(id => !selectedSet.has(id));

    const menuPayload = {
      emp_code: String(empId).trim(),
      menu_ids: selectedRoles.map(item => ({
        menu_id: Number(item.menu_id),
        can_be_delegated:
          item.can_be_delegated === true ||
            item.can_be_delegated === "true" ||
            item.can_be_delegated === "Y"
            ? "Y"
            : "N",
      })),
      available_menu_ids: availableIds,
      removed_menu_ids: removedIds,
      created_by: user?.emp_id,
      modified_by: user?.emp_id,
    };

    console.log("Submit payload summary:", {
      emp_code: menuPayload.emp_code,
      selected: selectedIds.length,
      removed: removedIds.length,
      removed_menu_ids: removedIds,
    });

    try {
      if (!menuPayload.menu_ids.length) {
        alert("Please select at least one menu permission.");
        return;
      }

      const menuResponse = await axios.post('/api/assignRoles/assignUserMenu', menuPayload);
      const msg = menuResponse?.data?.message || 'Menu permissions saved successfully!';
      alert(msg);

      // Reload from DB so UI matches what was saved
      await handleShowRoles();

      if (onSubmit) onSubmit();
    } catch (error) {
      console.error('Error saving menu permissions:', error);
      const msg =
        error?.response?.data?.message ||
        'Failed to save menu permissions. Please try again.';
      alert(msg);
    }
  };

  const handleReset = () => {
    setEmpId('');
    setCopyEmpId('');
    setUserPermission('');
    setTreeMenu([]);
    setSelectedRoles([]);
  };

  useEffect(() => {
    if (emp_id) {
      fetchLoggedInUserMenuDataTree();
      fetchUsersMap();
    }
  }, [emp_id]);

  if (!token) {
    return (
      <div className="mobileContainer">
        <p>You must be logged in to manage users.</p>
        <Button onClick={() => navigate("/")}>Go to Login</Button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div ref={topRef} />
      <h2 className={styles.heading}>Assign Roles</h2>

      <Card className="shadow-sm border-0">
        <Card.Body className="p-3 p-md-4">
          <Row className="g-3 align-items-start align-items-lg-end">
            <Col xs={12} lg={5}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  <i className="bi bi-person me-2"></i>
                  User Code
                </Form.Label>
                <div className="d-flex flex-column flex-sm-row gap-2">
                  <Form.Control
                    type="text"
                    value={empId}
                    onChange={(e) => setEmpId(e.target.value)}
                    placeholder="Enter user code"
                    className="flex-grow-1"
                  />
                  <Button
                    variant="primary"
                    onClick={handleShowRoles}
                    disabled={loadingMenus}
                    className="w-100 w-sm-auto"
                  >
                    Show Roles
                  </Button>
                </div>
              </Form.Group>
            </Col>

            <Col xs={12} lg={1} className="text-center mb-2">
              <FaArrowRightArrowLeft />
            </Col>

            <Col xs={12} lg={5}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  <i className="bi bi-copy me-2"></i>
                  Copy Permissions From
                </Form.Label>
                <div className="d-flex flex-column flex-sm-row gap-2">
                  <Form.Control
                    type="text"
                    value={copyEmpId}
                    onChange={(e) => setCopyEmpId(e.target.value)}
                    placeholder="Enter user code"
                    className="flex-grow-1"
                  />
                  <Button
                    variant="outline-primary"
                    onClick={handleCopyRoles}
                    disabled={loadingMenus}
                    className="w-100 w-sm-auto"
                  >
                    Copy Roles
                  </Button>
                </div>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Roles tree only after Show Roles / Copy Roles */}
      {userPermission && treeMenu.length > 0 && (
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="fw-semibold mt-3 mb-0">
              <i className="bi bi-diagram-3 me-2 text-primary"></i>
              Permissions assigned to user:{" "}
              <span className="text-primary">{formatUserLabel(userPermission)}</span>
              {copyEmpId && (
                <span className="text-muted ms-2" style={{ fontSize: 13 }}>
                  (copied from {formatUserLabel(copyEmpId)})
                </span>
              )}
            </h6>

            {selectedRoles.length > 0 && (
              <Badge bg="primary" pill>
                {selectedRoles.length} selected
              </Badge>
            )}
          </div>

          <RolesTreeStructure
            treeMenu={treeMenu}
            setSelectedRoles={setSelectedRoles}
          />
        </div>
      )}

      <div className="mt-3 d-flex flex-column flex-sm-row gap-2 justify-content-end">
        <Button
          variant="secondary"
          className="w-100 w-sm-auto order-2 order-sm-1"
          onClick={handleReset}
          style={{ minHeight: '44px' }}
        >
          <i className="bi bi-arrow-counterclockwise me-1"></i>
          Reset
        </Button>
        <Button
          variant="primary"
          className="w-100 w-sm-auto order-1 order-sm-2"
          onClick={handleRolesSubmit}
          disabled={!userPermission || selectedRoles.length === 0}
          style={{ minHeight: '44px' }}
        >
          <i className="bi bi-check2-circle me-1"></i>
          Submit
          {selectedRoles.length > 0 && (
            <Badge bg="light" text="dark" className="ms-2">
              {selectedRoles.length}
            </Badge>
          )}
        </Button>
      </div>


      <FloatingActions topRef={topRef} bottomRef={bottomRef} />
      <div ref={bottomRef} />
    </div>
  );
};

export default AssignRoles;
